import { HistoryIcon, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Loader } from '@/components/ai-elements/loader'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { deleteChat, loadChatsAsync } from '@/lib/chats'
import type { Chat } from '@/lib/types'

interface HistoryProps {
  onSelectChat?: (chatId: string) => void
}

export default function History({ onSelectChat }: HistoryProps) {
  const [chats, setChats] = useState<Chat[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      loadChatsAsync()
        .then((chats) => {
          setChats(chats)
        })
        .catch((error) => {
          console.error('Failed to load chat history', error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [isOpen])

  const handleDelete = async (chatId: string) => {
    if (deletingId) return

    setDeletingId(chatId)
    try {
      await deleteChat(chatId)
      setChats((prev) => prev.filter((chat) => chat.id !== chatId))
    } catch (error) {
      console.error('Failed to delete chat', error)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="History">
          <HistoryIcon />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] max-h-[90vh] flex flex-col">
        <DialogHeader className="text-left">
          <DialogTitle className="text-sm">Chat History</DialogTitle>
        </DialogHeader>
        <div className="max-h-[50vh] max-w-full overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader size={20} />
            </div>
          ) : chats.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">
              No chat history
            </div>
          ) : (
            <div className="space-y-1 min-w-0">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className="group flex flex-row w-full gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted/50 min-w-0 items-center cursor-pointer"
                  onClick={() => {
                    if (onSelectChat) {
                      onSelectChat(chat.id)
                      setIsOpen(false)
                    }
                  }}
                >
                  <span className="flex-1 min-w-0 truncate">{chat.title}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(chat.id)
                    }}
                    className="flex-shrink-0 gap-0 p-0 hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    disabled={deletingId === chat.id}
                    title="Delete chat"
                  >
                    <Trash2 size={8} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
